/* eslint-disable lingui/no-unlocalized-strings */
import {
  IS_LOCAL_CYPRESS,
  IS_LOCAL_DEV_NO_CY_NO_EMULATE,
  isPRPreview,
  KnownDomains,
} from '../../utils/constants';
import {
  Config,
  configAliases,
  getEnvKeyFromHostname,
  getPRDomainFromStorage,
  hostnameIsV2Tenant,
  localConfig,
} from '../base';
import { launchDarklyKeyMap } from '../launchDarklyKeyMap';
import { findEnvironmentByHostname } from './tenants.config';

/**
 * Registry-driven config builder.
 *
 * Builds the runtime `Config` for a hostname. The non-derivable values (flatfile
 * keys, LaunchDarkly key, envKey) are sourced from the tenant registry
 * (`tenants.config.ts`); every endpoint and keycloak value is derived by
 * templating the resolved hostname.
 *
 * The localhost / cypress / PR-preview branches reuse the shared primitives in
 * `./base`.
 */
export const buildConfigFromRegistry = (
  hostname: string,
): Config | undefined => {
  let base = typeof hostname === 'string' ? hostname : '';
  const foundFromResolutions = Object.entries(configAliases).find(
    ([, testArr]) => testArr.includes(base as KnownDomains),
  )?.[0];
  if (foundFromResolutions) {
    base = foundFromResolutions;
  }
  let useV2 = false;
  if (hostnameIsV2Tenant(hostname)) {
    base = hostname;
    useV2 = true;
  }
  if (base === 'localhost' || IS_LOCAL_CYPRESS) {
    // If we know we are in local, on a regular browser (not cy), AND that the
    // contributor does not want to emulate an env, we use internal dev config.
    if (IS_LOCAL_DEV_NO_CY_NO_EMULATE) {
      return buildConfigFromRegistry('dev.mm100.mastermindtms.com');
    }
    return localConfig;
  } else if (isPRPreview(base)) {
    return buildConfigFromRegistry(
      getPRDomainFromStorage() ?? 'dev.mm100.mastermindtms.com',
    );
  } else if (!base.match(/mastermindtms\.com/)) {
    return undefined;
  }

  // Values that are normally sourced from the registry. A valid
  // `mastermindtms.com` hostname that is NOT a registered environment still gets
  // a templated config here (rather than `undefined`). This is required by apps
  // that resolve their real config outside the registry — Connect Carrier /
  // Connect Customer run on `connect-*.hub.mastermindtms.com` hosts that are
  // intentionally absent from the tenant registry (see `./connect`), and
  // `src/config/index.ts` throws if `rawConfig` is undefined. Returning a
  // templated config keeps those apps booting; their crossAppConfig is
  // overridden afterward regardless.
  const registryEnv = findEnvironmentByHostname(hostname)?.environment;

  const isDev = Boolean(base.match(/^dev\./));
  const isTest = Boolean(base.match(/^test\./));
  const isPreview = isDev || isTest;

  let tenantId = base
    .replace(/dev\./, '')
    .replace(/test\./, '')
    .replace(/\.mastermindtms\.com/, '');
  if (useV2) {
    tenantId = hostname;
  }

  // LaunchDarkly key: prefer the registry value; otherwise derive it for
  // non-registry hostnames.
  let launchDarklyKey = registryEnv?.launchDarklyKey;
  if (launchDarklyKey === undefined) {
    launchDarklyKey = launchDarklyKeyMap.production;
    if (tenantId === 'mm100' || tenantId === 'test.tango.mastermindtms.com') {
      launchDarklyKey = isDev
        ? launchDarklyKeyMap.internalDev
        : launchDarklyKeyMap.internalTest;
    } else if (isPreview) {
      launchDarklyKey = launchDarklyKeyMap.preview;
    }
  }

  let keycloakUri = `https://id.${tenantId}${
    isPreview ? 'non' : ''
  }prod.mastermindtms.com/auth`;

  if (useV2) {
    const kcBase = base
      .replace(/dev\./, '')
      .replace(/test\./, '')
      .replace(/\.mastermindtms\.com/, '');
    keycloakUri = `https://id.${kcBase}${
      isPreview ? '-nonprod' : ''
    }.mastermindtms.com/auth`;
  }

  const obj: Config = {
    launchDarklyKey: launchDarklyKey ?? '',
    apiEndpoint: `https://graphql.${base}/`,
    minionApiRestEndpoint: `https://minion-api.${base}`,
    dataDictionaryEndpoint: `https://minion-api.${base}/graphql`,
    subscriptionApiEndpoint: `wss://subscriptions.${base}/graphql`,
    uploadsApiEndpoint: `https://uploads.${base}`,
    restApiEndpoint: `https://rest.${base}`,
    documentApiEndpoint: `https://document-api.${base}`,
    keycloakUri,
    keycloakClientId: `https://${base}/`,
    keycloakRealm: base,
    caenLockingApiEndpoint: `https://caenlocking-api.${base}`,
    emailTenderingEndpoint: `https://tendering-email-api.${base}`,
    aiEndpoint: `https://ai.${base}`,
    alchemyDataEndpoint: `https://alchemy-data-api.${base}`,
    regionsDataEndpoint: `https://regions-v2-api.${base}`,
    flatFileLicenseKey: registryEnv?.flatFileLicenseKey ?? '',
    flatFileApiEndPoint: `https://flatfile.${base}`,
    ltlPricingMatrix: `https://ltl-pricing-matrix.${base}`,
    masterbidEndpoint: `https://masterbid.${base}`,
    flatFilePlatformPublishableKey: registryEnv?.flatFilePublishableKey ?? '',
    flatFilePlatformEnvironmentId: registryEnv?.flatFileEnvironmentId ?? '',
    envKey: registryEnv?.envKey ?? getEnvKeyFromHostname(hostname),
  };
  if (tenantId === 'sn100' && isPreview) {
    obj.keycloakUri = 'https://id.clientnonprod.mastermindtms.com/auth';
  }
  return obj;
};
